export type TipoOrdenacao = 'alpha' | 'date' | 'valor';
export type DirecaoOrdenacao = 'asc' | 'desc';

function paraData(valor: any): Date {
  if (!valor) return new Date(0);
  if (valor instanceof Date) return valor;

  const texto = String(valor);
  if (/^\d{4}-\d{2}-\d{2}/.test(texto)) return new Date(texto);
  if (/^\d{2}\/\d{2}\/\d{4}/.test(texto)) {
    const [dia, mes, ano] = texto.split('/');
    return new Date(+ano, +mes - 1, +dia);
  }

  return new Date(texto);
}

/** Comparador reutilizável — base do UtilsService.sortArray e do OrdenacaoTabela. */
export function comparadorOrdenacao(
  key: string,
  tipo: string = 'alpha',
  reverse = false,
): (a: any, b: any) => number {
  return (a: any, b: any) => {
    let valA = a?.[key];
    let valB = b?.[key];

    if (tipo === 'date') {
      valA = paraData(valA);
      valB = paraData(valB);
    } else if (tipo === 'valor') {
      valA = Number(valA) || 0;
      valB = Number(valB) || 0;
    } else {
      valA = (valA ?? '').toString().toLowerCase();
      valB = (valB ?? '').toString().toLowerCase();
    }

    if (valA < valB) return reverse ? 1 : -1;
    if (valA > valB) return reverse ? -1 : 1;
    return 0;
  };
}

/**
 * Guarda o estado de ordenação de uma tabela (coluna + direção) e aplica a
 * ordenação na lista. Use junto do `th[appThOrdenavel]`:
 *
 *   ordenacao = new OrdenacaoTabela({ followUpFormat: 'date' });
 *
 *   ordenarPor(coluna: string): void {
 *     this.ordenacao.alternar(coluna);
 *     this.lista = this.ordenacao.aplicar(this.lista);
 *   }
 */
export class OrdenacaoTabela {
  coluna: string | null = null;
  direcao: DirecaoOrdenacao = 'asc';

  /** Tipo por coluna. O que não for informado é tratado como 'alpha'. */
  constructor(private readonly tipos: Record<string, TipoOrdenacao> = {}) {}

  alternar(coluna: string): void {
    if (this.coluna === coluna) {
      this.direcao = this.direcao === 'asc' ? 'desc' : 'asc';
      return;
    }

    this.coluna = coluna;
    this.direcao = 'asc';
  }

  limpar(): void {
    this.coluna = null;
    this.direcao = 'asc';
  }

  /** Retorna uma nova lista ordenada; sem coluna ativa, devolve a original. */
  aplicar<T>(lista: T[]): T[] {
    if (!this.coluna) return lista;

    return [...lista].sort(
      comparadorOrdenacao(this.coluna, this.tipos[this.coluna] ?? 'alpha', this.direcao === 'desc'),
    );
  }
}
